/**
 * Product Information Version Comparison Component
 * 
 * Displays side-by-side comparison of two versions of product information
 * with highlighted differences and rollback capabilities.
 */

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  ProductInfoVersion,
  ProductInfoComparison
} from '../types/enhanced-product-info.types';
import { enhancedProductService } from '../services/enhanced-product.service';

interface ProductInfoVersionComparisonProps {
  batchId: string;
  versions: ProductInfoVersion[];
  onRollback?: (rollbackResult: any) => void;
  onClose?: () => void;
}

export const ProductInfoVersionComparison: React.FC<ProductInfoVersionComparisonProps> = ({
  batchId,
  versions,
  onRollback,
  onClose
}) => {
  const [selectedVersion1, setSelectedVersion1] = useState<number>(
    versions.length > 1 ? versions[1].version : versions[0].version
  );
  const [selectedVersion2, setSelectedVersion2] = useState<number>(
    versions[0].version
  );
  const [comparison, setComparison] = useState<ProductInfoComparison | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackReason, setRollbackReason] = useState('');
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);

  useEffect(() => {
    if (selectedVersion1 !== selectedVersion2) {
      loadComparison();
    }
  }, [selectedVersion1, selectedVersion2]);

  const loadComparison = async () => {
    try {
      setLoading(true);
      setError(null);

      const comparisonResult = await enhancedProductService.compareVersions(
        batchId,
        selectedVersion1,
        selectedVersion2
      );

      setComparison(comparisonResult);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to compare versions');
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async () => {
    if (!rollbackReason.trim()) {
      alert('Please provide a reason for the rollback');
      return;
    }

    try {
      setRollbackLoading(true);

      const rollbackResult = await enhancedProductService.rollbackToVersion(
        batchId,
        selectedVersion1,
        'current-user-id', // This should come from auth context
        rollbackReason
      );

      setShowRollbackDialog(false);
      setRollbackReason('');

      if (onRollback) {
        onRollback(rollbackResult);
      }

      alert('Rollback completed successfully!');

    } catch (err) {
      alert(`Rollback failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRollbackLoading(false);
    }
  };

  const renderDifference = (diff: any, index: number) => {
    const isSignificant = diff.significance === 'high';
    
    return (
      <div
        key={index}
        className={`p-4 rounded-lg border ${
          isSignificant 
            ? 'border-red-200 bg-red-50' 
            : 'border-yellow-200 bg-yellow-50'
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              isSignificant 
                ? 'bg-red-100 text-red-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {diff.section.toUpperCase()}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {diff.field}
            </span>
          </div>
          <span className={`text-xs ${
            isSignificant ? 'text-red-600' : 'text-yellow-600'
          }`}>
            {diff.significance} impact
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Version 1 (Older) */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Version {selectedVersion1} (Older)
            </h4>
            <div className="bg-white p-3 rounded border">
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-auto max-h-32">
                {JSON.stringify(diff.oldValue, null, 2)}
              </pre>
            </div>
          </div>

          {/* Version 2 (Newer) */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Version {selectedVersion2} (Newer)
            </h4>
            <div className="bg-white p-3 rounded border">
              <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-auto max-h-32">
                {JSON.stringify(diff.newValue, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getVersionInfo = (versionNumber: number) => {
    return versions.find(v => v.version === versionNumber);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Version Comparison
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Version Selectors */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Version 1 Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compare Version (Older)
            </label>
            <select
              value={selectedVersion1}
              onChange={(e) => setSelectedVersion1(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {versions.map((version) => (
                <option key={version.version} value={version.version}>
                  Version {version.version} - {format(new Date(version.createdAt), 'MMM dd, yyyy HH:mm')}
                </option>
              ))}
            </select>
            {getVersionInfo(selectedVersion1) && (
              <div className="mt-2 text-sm text-gray-600">
                Created by: {getVersionInfo(selectedVersion1)?.createdBy}
              </div>
            )}
          </div>

          {/* Version 2 Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              With Version (Newer)
            </label>
            <select
              value={selectedVersion2}
              onChange={(e) => setSelectedVersion2(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {versions.map((version) => (
                <option key={version.version} value={version.version}>
                  Version {version.version} - {format(new Date(version.createdAt), 'MMM dd, yyyy HH:mm')}
                </option>
              ))}
            </select>
            {getVersionInfo(selectedVersion2) && (
              <div className="mt-2 text-sm text-gray-600">
                Created by: {getVersionInfo(selectedVersion2)?.createdBy}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex items-center space-x-4">
          <button
            onClick={loadComparison}
            disabled={loading || selectedVersion1 === selectedVersion2}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Comparing...' : 'Compare Versions'}
          </button>

          {comparison && selectedVersion1 < selectedVersion2 && (
            <button
              onClick={() => setShowRollbackDialog(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
            >
              Rollback to Version {selectedVersion1}
            </button>
          )}
        </div>
      </div>

      {/* Comparison Results */}
      <div className="px-6 py-4">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Comparing versions...</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {comparison && !loading && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Comparison Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Total Changes:</span>
                  <span className="ml-2">{comparison.summary.totalChanges}</span>
                </div>
                <div>
                  <span className="font-medium">Sections Modified:</span>
                  <span className="ml-2">{comparison.summary.changedSections.join(', ')}</span>
                </div>
                <div>
                  <span className="font-medium">Significant Changes:</span>
                  <span className="ml-2">{comparison.summary.significantChanges.length}</span>
                </div>
              </div>
            </div>

            {/* Differences */}
            {comparison.differences.length > 0 ? (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Detailed Changes</h3>
                {comparison.differences.map((diff, index) => renderDifference(diff, index))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No differences found between the selected versions.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rollback Dialog */}
      {showRollbackDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Rollback
            </h3>
            
            <p className="text-sm text-gray-600 mb-4">
              You are about to rollback to version {selectedVersion1}. This will replace the current 
              product information with the data from the selected version.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rollback (required)
              </label>
              <textarea
                value={rollbackReason}
                onChange={(e) => setRollbackReason(e.target.value)}
                placeholder="Please explain why you are rolling back..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowRollbackDialog(false)}
                disabled={rollbackLoading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRollback}
                disabled={rollbackLoading || !rollbackReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rollbackLoading ? 'Rolling back...' : 'Confirm Rollback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};